import { CopyUserComponent } from '../../dialogs/copy-user/copy-user.component';
import { AdminService, UserInfoResolver, DialogService, UserService } from '@miq/platform';
import { DataSource } from '@angular/cdk/collections';
import {
  Component,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, Validators, FormsModule } from '@angular/forms';
import { CbreAlertService } from '@cbre/alerts';
import { AddLocationComponent } from '../../dialogs/add-location/add-location.component';
import { isNotNull } from '@miq/utils';
import { DataObject, IUserSettings, ILocationPermission, IUser } from '@miq/shared-models';
import { L10nTranslationService, L10nTranslationModule } from 'angular-l10n';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { TitleCasePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import {
  CdkTable,
  CdkColumnDef,
  CdkHeaderCellDef,
  CdkHeaderCell,
  CdkCellDef,
  CdkCell,
  CdkHeaderRowDef,
  CdkHeaderRow,
  CdkRowDef,
  CdkRow,
} from '@angular/cdk/table';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatIconButton } from '@angular/material/button';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-location-permissions',
  templateUrl: './location-permissions.component.html',
  styleUrl: './location-permissions.component.scss',
  imports: [
    L10nTranslationModule,
    MatIcon,
    CdkTable,
    CdkColumnDef,
    CdkHeaderCellDef,
    CdkHeaderCell,
    CdkCellDef,
    CdkCell,
    MatTooltip,
    MatRadioGroup,
    FormsModule,
    MatRadioButton,
    MatIconButton,
    CdkHeaderRowDef,
    CdkHeaderRow,
    CdkRowDef,
    CdkRow,
    TitleCasePipe
],
})
export class LocationPermissionsComponent implements OnInit, OnDestroy {
  readonly locationPermissionsInput = input<ILocationPermission[]>([], { alias: 'locationPermissions' });
  public locationPermissions: ILocationPermission[] = [];
  requestMode = input(false);
  pageType = input<string>();
  userId = input<string>();
  isAddUser = input<boolean>();
  isPendingUser = input<boolean>();
  copyResultEvent = output<any>();
  public formReady = output<FormControl>();
  public locationsFormControl: FormControl<ILocationPermission[] | null>;
  public userInfo: IUser;
  private readonly dataSubject = signal<ILocationPermission[]>([]);
  public locationPermissionsDataSource: DataSource<ILocationPermission>;
  public recordsArray: ILocationPermission[] = [];
  public userSettings: IUserSettings;
  public displayedColumns: string[] = [
    'globalRegion',
    'division',
    'country',
    'market',
    'submarket',
    'productType',
    'businessJustification',
    'confidentiality',
    'status',
    'buttons',
  ];
  private readonly logger = inject<CbreLogger>(CbreLoggerToken);
  private readonly notificationService = inject(DialogService);
  private readonly httpService = inject(AdminService);
  private readonly userService = inject(UserService);
  private readonly resolver = inject(UserInfoResolver);
  private readonly cbreAlertService = inject(CbreAlertService);
  private readonly formBuilder = inject(FormBuilder);
  public translationService = inject(L10nTranslationService);
  constructor() {
    this.locationsFormControl = this.formBuilder.control(this.locationPermissions, [
      Validators.required,
      Validators.minLength(1),
    ]);
    this.userInfo = this.resolver.resolve();
    this.locationPermissionsDataSource = new LocationPermissionsDataSource(toObservable(this.dataSubject));
    effect(() => {
      const v = this.locationPermissionsInput();
      if (v !== undefined) {
        this.locationPermissions = v;
        this.locationsFormControl.patchValue(this.locationPermissions);
        this.dataSubject.set(this.locationPermissions);
        this.assignRowID(this.locationPermissions);
      }
    });
  }
  ngOnInit() {
    const locationPermissionsV = this.locationPermissionsInput();
    if (locationPermissionsV !== undefined) this.locationPermissions = locationPermissionsV;
    this.formReady.emit(this.locationsFormControl);
    if (this.locationPermissions) {
      this.dataSubject.set(this.locationPermissions);
    } else {
      this.locationPermissions = [];
    }
    if (!this.isAddUser() && this.pageType() === 'Admin') {
      this.displayedColumns.splice(4, 0, 'businessJustification');
    }
    if (this.pageType() === 'Admin' && this.isPendingUser()) {
      this.displayedColumns.splice(5, 0, 'Approve/Reject');
    }
  }
  public openDialog() {
    const data = { pageType: this.pageType() };
    this.notificationService
      .openDialog(AddLocationComponent, data, { disableClose: true, panelClass: 'table-modals' })
      .pipe(filter(result => !!result))
      .subscribe(result => {
        const newPermissionsArray = [];
        result.forEach(record => {
          if (!this.findPermissionExists(this.locationPermissions, record)) {
            this.locationPermissions.push(record);
            newPermissionsArray.push(record);
            this.locationsFormControl.setValue(this.locationPermissions);
          }
        });
        if (this.pageType() && this.pageType() === 'user' && !this.requestMode() && newPermissionsArray.length > 0) {
          this.httpService.addPermissions(this.userId(), newPermissionsArray).subscribe(
            () => {
              this.userService
                .getUserPermissions(this.userId())
                .pipe(take(1))
                .subscribe(userPermissions => {
                  this.locationPermissions = userPermissions;
                  this.dataSubject.set([...this.locationPermissions]);
                });
            },
            err => {
              this.logger.error(`Error adding permissions: ${err.message}`);
            }
          );
        } else {
          this.assignRowID(this.locationPermissions);
          this.dataSubject.set([...this.locationPermissions]);
        }
      });
  }
  /*
   * Delete user permissions
   */
  public deletePermission(userId: number, permissionsID: number, rowId: number) {
    if (permissionsID && this.pageType() && this.pageType() === 'user' && !this.requestMode()) {
      this.httpService
        .deletePermissions(userId, permissionsID)
        .pipe(take(1))
        .subscribe(result => {
          this.userService
            .getUserPermissions(userId)
            .pipe(take(1))
            .subscribe(response => {
              this.locationPermissions = response;
              this.dataSubject.set(this.locationPermissions);
            });
        });
    } else {
      this.locationPermissions = this.locationPermissions.filter(permission => permission.rowId !== rowId);
      this.locationsFormControl.setValue(this.locationPermissions);
      this.formReady.emit(this.locationsFormControl);
      this.dataSubject.set(this.locationPermissions);
    }
  }
  /*
   * edit user permissions
   */
  public edit(locationPermission: ILocationPermission) {
    const data = { locationPermission, pageType: this.pageType() };
    this.notificationService
      .openDialog(AddLocationComponent, data, { disableClose: true, panelClass: 'table-modals' })
      .subscribe(result => {
        if (result) {
          result.map(record => {
            const permission = this.findPermissionExists(this.locationPermissions, record);
            if (!permission) {
              this.locationPermissions.splice(locationPermission.rowId, 1);
              return this.locationPermissions.push(record);
            }
            this.locationPermissions = this.locationPermissions.map(locPerm =>
              locPerm.id === record.id ? { ...locPerm, ...record } : locPerm
            );
          });
          this.dataSubject.set([...this.locationPermissions]);
        }
        this.assignRowID(this.locationPermissions);
      });
  }
  /**
   * check for duplicate records
   */
  public findPermissionExists(locationPermission: ILocationPermission[], record: ILocationPermission): any {
    if (locationPermission && locationPermission.length > 0) {
      for (const object of locationPermission) {
        if (
          object.globalRegionId === record.globalRegionId &&
          object.divisionId === record.divisionId &&
          object.countryId === record.countryId &&
          object.marketId === record.marketId &&
          object.subMarketId === record.subMarketId &&
          object.districtId === record.districtId &&
          object.neighborhoodId === record.neighborhoodId &&
          object.productTypeId === record.productTypeId
        ) {
          return object;
        }
      }
    }
    return null;
  }
  /**
   * assign row Id's to manage temporary state in UI for location permissions
   */
  public assignRowID(locationPermission: ILocationPermission[]): void {
    if (locationPermission && locationPermission.length > 0) {
      locationPermission.forEach((record, index) => {
        record.rowId = index;
      });
    }
  }
  /**
   * Clone location permission from existing user
   */
  public cloneUser(): void {
    const options = {};
    this.notificationService.openDialog(CopyUserComponent, null, options).subscribe(result => {
      if (!!result) {
        const cloneType = this._getCloneType(result);
        this.httpService
          .cloneUser(result.email.toLowerCase(), cloneType)
          .pipe(take(1))
          .subscribe(
            response => {
              if (response && response.permissions && response.permissions.length > 0) {
                this.copyResultEvent.emit(response);
                response.permissions.forEach(record => {
                  record.updateBy = this.userInfo.emailAddress;
                  record.createBy = this.userInfo.emailAddress;
                  const permission = this.findPermissionExists(this.locationPermissions, record);
                  if (!permission) {
                    this.locationPermissions.push(record);
                  } else {
                    // Merge cloned values (e.g. confidentialityTypeId) into the matching row,
                    // keeping the current user's permission id so saves target the right record
                    this.locationPermissions = this.locationPermissions.map(locPerm =>
                      locPerm === permission ? { ...locPerm, ...record, id: locPerm.id } : locPerm
                    );
                  }
                });
                if (response.settings) {
                  this.userSettings = response.settings;
                }
              }
              this.dataSubject.set([...this.locationPermissions]);
              this.cbreAlertService.showSuccessSnackBar_l10n('shared.permissionsAdded');
            },
            // Show error message
            err => this.cbreAlertService.showErrorSnackBar(err.message)
          );
      }
    });
  }
  private _getCloneType(results: { profileSettings: DataObject; rolePermissions: DataObject }) {
    const { profileSettings, rolePermissions } = results;
    if ([profileSettings, rolePermissions].every(isNotNull)) {
      return 'both';
    } else if (isNotNull(profileSettings)) {
      return 'settings';
    } else if (isNotNull(rolePermissions)) {
      return 'permissions';
    }
    return null;
  }
  ngOnDestroy() {
    /** label translation purpose added */
  }
}
class LocationPermissionsDataSource implements DataSource<any> {
  constructor(private source$: Observable<any>) {}
  connect(): Observable<any> {
    return this.source$;
  }
  disconnect() {
    // This is intentional
  }
}
